/**
 * Top bar — logo, the ⌘K search trigger, the mode quick toggle, and the user
 * menu (doc 147 §5.1; §8.9.6 revised for theming).
 *
 * The mode toggle cycles between light and dark on click (the most-common
 * path). Setting `auto` (which follows the OS) requires the full tri-state
 * in the Settings view — by design, to keep the TopBar a single click.
 */
import { Icon } from '../components/Icon.js';
import { navigate } from '../state/hashRouter.js';
import type { InternalUser } from '../state/store.js';

export interface TopBarProps {
  user: InternalUser;
  /**
   * Concrete resolved mode (after `auto` collapses to dark or light). Drives
   * which icon the quick-toggle shows.
   */
  resolvedMode: 'dark' | 'light';
  onOpenSearch: () => void;
  onSignOut: () => void;
  /** Called with the *opposite* of `resolvedMode` when the user clicks the toggle. */
  onToggleMode: (next: 'dark' | 'light') => void;
  /** Whether the sidebar is currently collapsed. */
  sidebarCollapsed: boolean;
  /** Called when the user toggles the sidebar collapse state. */
  onToggleSidebar: () => void;
}

export function TopBar({
  user,
  resolvedMode,
  onOpenSearch,
  onSignOut,
  onToggleMode,
  sidebarCollapsed,
  onToggleSidebar,
}: TopBarProps): React.JSX.Element {
  const next = resolvedMode === 'dark' ? 'light' : 'dark';
  const toggleIcon = resolvedMode === 'dark' ? 'light_mode' : 'dark_mode';
  return (
    <header className="topbar">
      <button
        className="topbar-sidebar-toggle"
        onClick={onToggleSidebar}
        aria-label="Toggle sidebar"
        aria-expanded={!sidebarCollapsed}
      >
        <Icon name={sidebarCollapsed ? 'menu_open' : 'menu'} size="md" />
      </button>
      <button
        className="topbar-logo"
        onClick={() => navigate({ kind: 'welcome' })}
        aria-label="Atlas home"
      >
        Atlas
      </button>
      <span className="topbar-tagline">/ Brisarx data model</span>

      <button className="topbar-search" onClick={onOpenSearch} aria-label="Open search (Cmd+K)">
        <Icon name="search" size="sm" />
        <span style={{ flex: 1, textAlign: 'left' }}>Search entities, fields, paths…</span>
        <span className="topbar-kbd">⌘K</span>
      </button>

      <div className="topbar-spacer" />

      <button
        className="tree-row"
        style={{ width: 'auto' }}
        onClick={() => navigate({ kind: 'ask' })}
        aria-label="Ask Atlas"
        title="Ask Atlas"
      >
        <Icon name="auto_awesome" size="md" />
      </button>
      <button
        className="tree-row"
        style={{ width: 'auto' }}
        onClick={() => navigate({ kind: 'catalog' })}
        aria-label="Catalog"
        title="Domain Catalog"
      >
        <Icon name="apps" size="md" />
      </button>
      <button
        className="tree-row"
        style={{ width: 'auto' }}
        onClick={() => navigate({ kind: 'boards' })}
        aria-label="Boards"
        title="Boards"
      >
        <Icon name="dashboard_customize" size="md" />
      </button>
      <a
        className="tree-row"
        style={{ width: 'auto', textDecoration: 'none' }}
        href="/atlas-user-guide.html"
        target="_blank"
        rel="noopener"
        aria-label="Open the Atlas user guide"
        title="User guide"
      >
        <Icon name="help_outline" size="md" />
      </a>
      <button
        className="tree-row"
        style={{ width: 'auto' }}
        onClick={() => onToggleMode(next)}
        aria-label={`Switch to ${next} mode`}
        title={`Switch to ${next} mode`}
      >
        <Icon name={toggleIcon} size="md" />
      </button>
      {user.role === 'admin' && (
        <button
          className="tree-row"
          style={{ width: 'auto' }}
          onClick={() => navigate({ kind: 'settings' })}
          aria-label="Settings"
          title="Settings"
        >
          <Icon name="settings" size="md" />
        </button>
      )}
      <span className="mono" style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>
        {user.email} · {user.role}
      </span>
      <button
        className="tree-row"
        style={{ width: 'auto' }}
        onClick={onSignOut}
        aria-label="Sign out"
        title="Sign out"
      >
        <Icon name="logout" size="md" />
      </button>
    </header>
  );
}
